import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCombobox, SpecraDataTable, SpecraEmptyState, SpecraSearchField, SpecraTabBar, SpecraTag } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { FileTypeIcon } from '../../../components/ProjectDashboard/FileTypeIcon';
import { PROGRAM_DOCUMENT_SECTIONS, sectionForCategory } from '../_shared/constants/documentCategories';
import { ColumnMenu, useColumnView } from '../Deliverables/components/ColumnMenu';
import { DocumentProcessingStatus } from './DocumentProcessingStatus';
const ALL_TAB = 'all';
const sameTag = (a, b)=>a.toLowerCase() === b.toLowerCase();
/** The category as the row shows it; a blank one reads as Other, where it files. */ const typeOf = (doc)=>doc.category || 'Other';
const formatUpdated = (doc)=>doc.uploadedAt ? doc.uploadedAt.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }) : '—';
/**
 * A combobox that adds a tag from `suggestions` (and, with `onCreate`, a new
 * one). Its value stays empty: choosing an option adds it rather than
 * selecting it.
 */ export function TagPicker({ label, suggestions, onAdd, canCreate = false, testId }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        children: /*#__PURE__*/ _jsx(SpecraCombobox, {
            label: label,
            placeholder: label,
            searchPlaceholder: "Search or type a tag…",
            emptyMessage: "No tags",
            value: "",
            onValueChange: (tag)=>tag && onAdd(tag),
            options: suggestions.map((tag)=>({
                    value: tag,
                    label: tag
                })),
            onCreate: canCreate ? (query)=>onAdd(query) : undefined,
            createLabel: (query)=>`Add tag "${query}"`,
            triggerProps: {
                size: 'sm',
                variant: 'outline'
            },
            contentClassName: "min-w-64"
        })
    });
}
function DocumentTags({ doc, canEdit, suggestions, onSetTags }) {
    const addable = suggestions.filter((tag)=>!doc.tags.some((t)=>sameTag(t, tag)));
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-1.5",
        children: [
            doc.tags.map((tag)=>/*#__PURE__*/ _jsx(SpecraTag, {
                    "data-testid": Testids.deliveryDocs.documentTag,
                    onRemove: canEdit ? ()=>onSetTags(doc, doc.tags.filter((t)=>t !== tag)) : undefined,
                    removeLabel: `Remove tag ${tag}`,
                    children: tag
                }, tag)),
            canEdit && /*#__PURE__*/ _jsx(TagPicker, {
                label: "Add tag",
                suggestions: addable,
                canCreate: true,
                onAdd: (tag)=>doc.tags.some((t)=>sameTag(t, tag)) ? undefined : onSetTags(doc, [
                        ...doc.tags,
                        tag
                    ]),
                testId: Testids.deliveryDocs.documentTagAdd
            })
        ]
    });
}
export function DocumentsList({ documents, isLoading, canEdit, canDelete, onOpen, processingOf = (doc)=>doc.processingStatus, onRetry, onSetTags, onDelete }) {
    const [tab, setTab] = useState(ALL_TAB);
    const [query, setQuery] = useState('');
    const [tagFilter, setTagFilter] = useState(new Set());
    const sorted = useMemo(()=>[
            ...documents
        ].sort((a, b)=>Number(b.uploadedAt?.toDate() ?? 0) - Number(a.uploadedAt?.toDate() ?? 0)), [
        documents
    ]);
    const allTags = useMemo(()=>[
            ...new Set(documents.flatMap((d)=>d.tags))
        ].sort((a, b)=>a.localeCompare(b)), [
        documents
    ]);
    const inTab = sorted.filter((d)=>tab === ALL_TAB || sectionForCategory(typeOf(d)) === tab);
    const tabTags = [
        ...new Set(inTab.flatMap((d)=>d.tags))
    ].sort((a, b)=>a.localeCompare(b));
    const specs = [
        {
            id: 'title',
            label: 'Document',
            value: (d)=>d.title,
            sortOnly: true
        },
        {
            id: 'section',
            label: 'Section',
            value: (d)=>sectionForCategory(typeOf(d))
        },
        {
            id: 'type',
            label: 'Type',
            value: typeOf
        },
        {
            id: 'uploaded',
            label: 'Uploaded',
            kind: 'date',
            value: formatUpdated,
            sortValue: (d)=>d.uploadedAt?.toDate().toISOString() ?? '',
            sortOnly: true
        }
    ];
    const view = useColumnView(specs, inTab);
    const needle = query.trim().toLowerCase();
    const visible = view.apply(inTab).filter((d)=>(!needle || [
            d.title,
            d.fileName,
            ...d.tags
        ].some((text)=>text.toLowerCase().includes(needle))) && (tagFilter.size === 0 || d.tags.some((t)=>[
                ...tagFilter
            ].some((f)=>sameTag(f, t)))));
    const filtering = !!needle || tagFilter.size > 0 || view.filtered || view.sorted;
    const tabOptions = [
        {
            value: ALL_TAB,
            label: 'All Documents',
            count: documents.length
        },
        ...PROGRAM_DOCUMENT_SECTIONS.map((section)=>({
                value: section,
                label: section,
                count: documents.filter((d)=>sectionForCategory(typeOf(d)) === section).length
            }))
    ];
    const clearFilters = ()=>{
        view.reset();
        setQuery('');
        setTagFilter(new Set());
    };
    const columns = [
        {
            id: 'title',
            header: view.header('title'),
            cell: (doc)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex min-w-0 items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(FileTypeIcon, {
                            contentType: "",
                            filename: doc.fileName,
                            className: "size-7 shrink-0"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-0 space-y-0.5",
                            children: [
                                doc.fileId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "link",
                                    size: "sm",
                                    className: "h-auto whitespace-normal p-0 text-left",
                                    onClick: ()=>onOpen(doc),
                                    "data-testid": Testids.deliveryDocs.documentOpen,
                                    children: doc.title
                                }) : /*#__PURE__*/ _jsx("div", {
                                    children: doc.title
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "truncate text-xs text-muted-foreground",
                                    children: doc.fileName
                                })
                            ]
                        })
                    ]
                })
        },
        {
            id: 'section',
            header: view.header('section'),
            cell: (doc)=>sectionForCategory(typeOf(doc))
        },
        {
            id: 'type',
            header: view.header('type'),
            cell: (doc)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    children: typeOf(doc)
                })
        },
        {
            id: 'tags',
            header: /*#__PURE__*/ _jsx(ColumnMenu, {
                columnId: "tags",
                label: "Tags",
                options: tabTags,
                selected: tagFilter,
                onSelectedChange: setTagFilter
            }),
            cell: (doc)=>/*#__PURE__*/ _jsx(DocumentTags, {
                    doc: doc,
                    canEdit: canEdit,
                    suggestions: allTags,
                    onSetTags: onSetTags
                })
        },
        {
            id: 'uploaded',
            header: view.header('uploaded'),
            cell: (doc)=>/*#__PURE__*/ _jsx("span", {
                    className: "whitespace-nowrap text-muted-foreground",
                    children: formatUpdated(doc)
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (doc)=>/*#__PURE__*/ _jsx(DocumentProcessingStatus, {
                    status: processingOf(doc),
                    reason: doc.processingReason,
                    onRetry: onRetry?.(doc)
                })
        },
        ...canDelete ? [
            {
                id: 'actions',
                header: /*#__PURE__*/ _jsx("span", {
                    className: "sr-only",
                    children: "Actions"
                }),
                align: 'right',
                cell: (doc)=>/*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                        onClick: ()=>onDelete(doc),
                        "data-testid": Testids.deliveryDocs.documentDelete,
                        children: "Delete"
                    })
            }
        ] : []
    ];
    if (isLoading) return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.deliveryDocs.documentsTable,
        "aria-busy": "true"
    });
    if (documents.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.deliveryDocs.documentsTable,
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: "No documents yet",
                description: "Record a document to start this program's library."
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.deliveryDocs.documentsTable,
        children: [
            /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: "Document sections",
                value: tab,
                options: tabOptions,
                onValueChange: (next)=>{
                    setTab(next);
                    view.clearAll();
                    setTagFilter(new Set());
                },
                "data-testid": Testids.deliveryDocs.documentSectionTabs
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap items-center gap-3",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "min-w-64 flex-1",
                    children: /*#__PURE__*/ _jsx(SpecraSearchField, {
                        "aria-label": "Search documents",
                        placeholder: "Search title, file name or tag",
                        value: query,
                        onChange: (e)=>setQuery(e.target.value),
                        onClear: ()=>setQuery(''),
                        "data-testid": Testids.deliveryDocs.documentSearch
                    })
                })
            }),
            inTab.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: "No documents in this section"
            }) : visible.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: "No documents match",
                description: "Nothing here matches your search or filters.",
                action: filtering ? /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "outline",
                    onClick: clearFilters,
                    "data-testid": Testids.deliveryDocs.documentClearFilters,
                    children: "Clear filters"
                }) : undefined
            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: columns,
                rows: visible,
                getRowId: (doc)=>doc.id,
                rowTestId: Testids.deliveryDocs.documentRow
            })
        ]
    });
}
